/**
 * СТРАНИЦА КОМНАТЫ — правая прокручиваемая панель, как в Telegram Desktop.
 *
 * ПОЧЕМУ ПАНЕЛЬ, а не модалка и не отдельный маршрут (постановка Антона):
 * «настройки живут кнопками в шапке + модалкой, а нужна отдельная страница».
 * Панель справа не перекрывает ленту (модалка перекрывала), закрывается
 * крестиком, и всё управление комнатой собрано в одном месте по секциям.
 *
 * ИСТОЧНИКИ ДАННЫХ:
 *  - права — useRoomPermissions (permissions.ts), тот же объект, что читают
 *    шапка и композер. Нет флага — поле показывается НЕАКТИВНЫМ с пояснением
 *    (требование постановки: человек должен видеть, что настройка есть, но
 *    недоступна), а не скрывается молча;
 *  - уведомления — src/matrix/pushRules.ts (серверные push-правила, общие
 *    с Element: mute здесь действует и на телефоне);
 *  - пороги ролей — src/matrix/powerLevels.ts (чтение/запись m.room.power_levels);
 *  - участники и забаненные — существующий MemberPanel (kick/ban/unban/роли
 *    уже там, дублировать запрещено постановкой). Забаненные показываются
 *    ВНУТРИ MemberPanel (секция «Забанены» с разбаном) — отсюда на них
 *    ведёт строка-переход, а не второй список;
 *  - редактирование полей (название/аватар/тема/вход/история) — существующая
 *    модалка RoomSettings. Постановка разрешает оставить её внутренним
 *    редактором: вход в настройки теперь ОТСЮДА, а не из шапки.
 */
import { useRef, useState, type ReactNode } from "react";
import { PanelResizeHandle, usePanelWidth } from "./PanelResize";
import type { Messages } from "../i18n/messages";
import { useT, useTF } from "../i18n";
import { EventType, type MatrixClient, type Room } from "matrix-js-sdk";

import { useRoomPermissions } from "../matrix/permissions";
import { humanError, setRoomAvatar } from "../matrix/roomActions";
import { AVATAR_ACCEPT } from "../matrix/avatarImage";
import { useImagePicker, visuallyHiddenInput } from "./useImagePicker";
import { PickerNoteLine } from "./PickerNoteLine";
import { useAuthedMedia } from "../matrix/media";
import {
  powerLevelsOf,
  setPowerLevelThreshold,
  thresholdOf,
} from "../matrix/powerLevels";
import {
  setRoomPushMode,
  useRoomPushMode,
  type RoomPushMode,
} from "../matrix/pushRules";
import { isEncrypted } from "../matrix/rooms";
import { MemberPanel } from "./MemberPanel";
import { RoomSettings } from "./RoomSettings";
import {
  roomNotificationSetting,
  saveRoomNotificationSetting,
  SOUND_OPTIONS,
  type SoundId,
} from "../notifications/settings";
import { previewSound } from "../notifications/sound";
import { IconBell, IconBellOff, IconCamera } from "./icons";
import { s, useNarrowScreen } from "./styles";
import {
  FilesTab,
  LinksTab,
  MediaTab,
  TabStrip,
  useRoomAttachments,
  VoiceTab,
  type RoomTab,
} from "./RoomTabs";
import { color, colorOf, font, radius, space } from "./theme";

/** Какая «подстраница» сейчас в панели: корень (секции) или участники. */

/**
 * Слово «участник» в нужной форме. В русском три формы (1 участник,
 * 2 участника, 5 участников), в остальных языках — две, поэтому
 * выбор формы живёт в коде, а слова — в словаре.
 */
function membersWord(n: number, t: (k: keyof Messages) => string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return t("room.membersCountMany");
  const mod10 = n % 10;
  if (mod10 === 1) return t("room.membersCountOne");
  if (mod10 >= 2 && mod10 <= 4) return t("room.membersCountFew");
  return t("room.membersCountMany");
}

export function RoomPage({
  client,
  room,
  canInvite,
  onInvite,
  onRoomLink,
  onLeave,
  onToggleFavourite,
  onClose,
}: {
  client: MatrixClient;
  room: Room;
  canInvite: boolean;
  onInvite: () => void;
  /** «Ссылка на комнату / Пригласить по ссылке» (Антон 16.10 10:33). */
  onRoomLink?: () => void;
  /** Выход из комнаты: открывает СУЩЕСТВУЮЩИЙ диалог подтверждения в App. */
  onLeave: () => void;
  onToggleFavourite: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const perms = useRoomPermissions(client, room);
  const encrypted = isEncrypted(room);
  const pinned = Boolean(room.tags?.["m.favourite"]);
  // Модалка-редактор полей (бывшие «Настройки» из шапки). Живёт поверх панели.
  const [showSettings, setShowSettings] = useState(false);
  // Активный таб (образец Антона 10.09: Участники · Медиа · Ссылки · Голосовые).
  // По умолчанию открыт таб «Участники», а не «Медиа» (Антон 10.09 17:33).
  // Медиа требуют скана истории и первые секунды отдают пустоту, а часто
  // и вовсе ничего не находят — человек открывал страницу и видел под
  // полоской табов пустое место, пока сам не ткнёт в какой-нибудь таб.
  // Состав комнаты есть всегда и готов сразу: это единственный таб, который
  // гарантированно не пустой, и он же первый в полоске.
  const [tab, setTab] = useState<RoomTab>("members");
  // Один проход по истории на все три «содержательных» таба: Matrix не отдаёт
  // списка вложений комнаты, его собирают листанием таймлайна — делать это
  // трижды было бы втрое дороже без всякой пользы.
  const { scan, busy, deeper } = useRoomAttachments(client, room);
  // На узком экране (≤640px) панель идёт во всю ширину: жёсткие 360px
  // оставляли ленте щель в палец, а ряд из четырёх действий и сетка медиа
  // по три в ряд упирались в стенки. См. useNarrowScreen в styles.ts.
  const narrow = useNarrowScreen();
  const [panelWidth, setPanelWidth] = usePanelWidth("roomPage", 360, 280, 720);

  return (
    <aside
      style={{
        ...s.roomPage,
        width: panelWidth,
        flex: `0 0 ${panelWidth}px`,
        position: "relative",
        ...(narrow
          ? { width: "100%", flex: "1 1 auto", borderLeft: "none" }
          : null),
      }}
      data-testid="room-page"
      aria-label={t("room.pageLabel")}
      /* alg-room-page: на ≤640px мобильный CSS делает панель fixed на
         весь экран поверх ленты (спека 2026-09-20, этап 2). */
      className={narrow ? "alg-room-page" : undefined}
    >
      {!narrow && (
        <PanelResizeHandle
          edge="left"
          width={panelWidth}
          onResize={setPanelWidth}
          onReset={() => setPanelWidth(360)}
          testId="resize-room-page"
        />
      )}
      {/* Шапка панели: только крестик. Никаких действий — они в секциях. */}
      <div
        style={{
          ...s.roomHeader,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: `${space.md}px ${space.lg}px`,
        }}
      >
        <button
          type="button"
          style={{
            ...s.iconButton,
            transition: "background 0.15s, color 0.15s",
          }}
          onClick={onClose}
          data-testid="room-page-close"
          title={t("common.close")}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = color.hover;
            e.currentTarget.style.color = color.text;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = color.textDim;
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
        <PageHead client={client} room={room} encrypted={encrypted} canEditAvatar={perms.canChangeAvatar} />

        {/* Быстрые действия под шапкой — как в Telegram: избранное и
            участники. Приглашение живёт внутри MemberPanel, но постановка
            просит кнопку «Пригласить» в секции участников И здесь — даём
            одну строку, которая открывает тот же диалог. */}
        {/* РЯД ЧЕТЫРЁХ ДЕЙСТВИЙ — как в образце (Add · Mute · Report · Leave).
            «Report» из телеграмного набора заменён на «В избранное»: жалобы
            на комнату в ALG некуда отправлять, кнопка-обманка хуже её
            отсутствия. Приглушение — настоящее, через серверные push-правила,
            поэтому действует и на телефоне. */}
        <QuickActions
          client={client}
          room={room}
          pinned={pinned}
          canInvite={canInvite}
          onInvite={onInvite}
          onToggleFavourite={onToggleFavourite}
          onLeave={onLeave}
        />

        {/* ССЫЛКА НА КОМНАТУ — отдельной строкой под рядом действий, а не
            пятой плиткой: ряд из четырёх повторяет образец Антона, пятая
            плитка его бы сломала. Пространства ссылку не получают. */}
        {onRoomLink && !room.isSpaceRoom() && (
          <div style={{ padding: `0 ${space.md}px ${space.md}px` }}>
            <button
              type="button"
              onClick={onRoomLink}
              data-testid="room-page-link"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${color.border}`,
                borderRadius: radius.md,
                background: color.bg,
                color: color.accent,
                font: "inherit",
                cursor: "pointer",
                textAlign: "center",
              }}
            >
              🔗 {t("room.linkMenu")}
            </button>
          </div>
        )}

        <TabStrip active={tab} onSelect={setTab} />

        {/* Участники — ТАКОЙ ЖЕ таб, как остальные, а не подмена всего экрана.
            Баг Антона 17:25 «нужно месте а не весь фрейм»: раньше клик по этому
            табу делал `setPane("members")` и возвращал MemberPanel ВМЕСТО страницы:
            исчезали аватар, название, ряд действий и сама полоска табов —
            перейти на «Медиа» было нельзя, только крестик и заново. Теперь список
            рисуется внутри таба (`embedded`), шапка и табы остаются на месте. */}
        {tab === "members" ? (
          <MemberPanel
            client={client}
            room={room}
            canInvite={canInvite}
            onInvite={onInvite}
            onClose={onClose}
            embedded
          />
        ) : scan === null ? (
          <ScanSkeleton />
        ) : tab === "media" ? (

          <MediaTab client={client} scan={scan} busy={busy} onDeeper={deeper} />
        ) : tab === "files" ? (
          <FilesTab client={client} scan={scan} busy={busy} onDeeper={deeper} />
        ) : tab === "links" ? (
          <LinksTab client={client} scan={scan} busy={busy} onDeeper={deeper} />
        ) : (
          <VoiceTab client={client} scan={scan} busy={busy} onDeeper={deeper} />
        )}

        {/* АДМИНСКАЯ ЧАСТЬ — СВЁРНУТА ПО УМОЛЧАНИЮ (Антон 10.09 17:26:
            «отсюда и ниже надо скрыть»).

            Настройки комнаты, пороги прав и статус шифрования нужны, но это
            админские вещи: их открывают раз в месяц, а занимали они больше
            экрана, чем всё остальное вместе взятое. В образце их на странице нет
            вовсе. Удалять нельзя — второго входа к ним не существует, поэтому
            скрыты под одну строку-раскрывашку, а не выброшены.

            Перед этим (баг 19:20) страница дублировала сама себя: уведомления
            были и кнопкой сверху, и секцией снизу, участники — и табом, и
            строкой, а таб «Файлы» показывал файлы над заглушкой «в разработке».
            Те дубли удалены совсем. */}
        <AdvancedDisclosure>

        <SettingsSection
          client={client}
          room={room}
          canEditSomething={
            perms.canChangeName ||
            perms.canChangeTopic ||
            perms.canChangeAvatar ||
            perms.canChangeJoinRules ||
            perms.canChangeHistoryVisibility
          }
          onOpenSettings={() => setShowSettings(true)}
        />

        <SectionDivider />

        <PowerLevelsSection
          client={client}
          room={room}
          canChange={perms.canChangePowerLevels}
          myPowerLevel={perms.myPowerLevel}
        />

        <SectionDivider />

        {/* Шифрование: crypto в клиенте выключен намеренно. Показываем
            честный статус и НЕ предлагаем включить (постановка). */}
        <div style={s.sectionLabel}>{t("room.encryptionWord")}</div>
        <div
          style={{ padding: `0 ${space.xl}px ${space.lg}px`, fontSize: font.small, color: color.text }}
          data-testid="page-encryption"
        >
          {encrypted ? (
            <>{t("room.encryptionPageEnabled")}</>
          ) : (
            <>{t("room.encryptionWord")}<b>{t("room.offWord")}</b>{t("room.encDisabledTail")}</>
          )}
        </div>
        </AdvancedDisclosure>

        <div style={{ height: space.xl }} />
      </div>

      {showSettings && (
        <RoomSettings client={client} room={room} onClose={() => setShowSettings(false)} />
      )}
    </aside>
  );
}

/** Большой аватар + название + число участников + тема. */
/**
 * Ряд четырёх действий под шапкой — образец Антона 10.09.
 *
 * «Пожаловаться» из телеграмного набора сознательно не переносим: в ALG нет
 * адресата таких жалоб, и кнопка вела бы в никуда. На её место — избранное,
 * которое реально работает.
 */
function QuickActions({
  client,
  room,
  pinned,
  canInvite,
  onInvite,
  onToggleFavourite,
  onLeave,
}: {
  client: MatrixClient;
  room: Room;
  pinned: boolean;
  canInvite: boolean;
  onInvite: () => void;
  onToggleFavourite: () => void;
  onLeave: () => void;
}) {
  const t = useT();
  const mode = useRoomPushMode(client, room.roomId);
  const [busy, setBusy] = useState(false);

  const choose = async (next: RoomPushMode) => {
    if (next === mode) return;
    setBusy(true);
    try {
      await setRoomPushMode(client, room.roomId, next);
    } catch {
      // Ошибку с текстом показывает секция «Уведомления» ниже — второй
      // текст об одной и той же ошибке только мешает.
    } finally {
      setBusy(false);
    }
  };

  type Item =
    | { kind?: "button"; icon: string; label: string; onClick: () => void; off?: boolean; testId: string }
    | { kind: "menu"; testId: string };
  const items: Item[] = [
    {
      icon: "＋",
      label: t("room.add"),
      onClick: onInvite,
      off: !canInvite,
      testId: "quick-invite",
    },
    // Место кнопки занимает МЕНЮ — постановка Антона 15:40: «вместо mute
    // выпадающее меню с вариантами нотификаций». Три режима вместо
    // двухпозиционного переключателя: включено / только упоминания / тихо.
    { kind: "menu", testId: "quick-notify" },
    {
      icon: pinned ? "★" : "☆",
      label: pinned ? t("room.removeFavourite") : t("room.favouriteMenu"),
      onClick: onToggleFavourite,
      testId: "quick-favourite",
    },
    {
      icon: "⎋",
      label: room.isSpaceRoom() ? t("room.spaceLeave") : t("common.leave"),
      onClick: onLeave,
      testId: "quick-leave",
    },
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 6,
        padding: `0 ${space.md}px ${space.md}px`,
      }}
    >
      {items.map((it) =>
        it.kind === "menu" ? (
          <NotifyMenu
            key={it.testId}
            roomId={room.roomId}
            mode={mode}
            busy={busy}
            onChoose={choose}
            testId={it.testId}
          />
        ) : (
        <button
          key={it.testId}
          type="button"
          onClick={it.onClick}
          disabled={it.off}
          data-testid={it.testId}
          title={it.off ? t("room.insufficientPermissions") : it.label}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 4,
            padding: "10px 2px",
            border: `1px solid ${color.border}`,
            borderRadius: radius.md,
            background: color.bg,
            color: it.off ? color.textDim : color.accent,
            cursor: it.off ? "default" : "pointer",
            opacity: it.off ? 0.55 : 1,
            transition: "background 0.15s, border-color 0.15s",
          }}
          onMouseEnter={(e) => {
            if (!it.off) {
              e.currentTarget.style.background = color.hover;
              e.currentTarget.style.borderColor = color.accent;
            }
          }}
          onMouseLeave={(e) => {
            if (!it.off) {
              e.currentTarget.style.background = color.bg;
              e.currentTarget.style.borderColor = color.border;
            }
          }}
        >
          <span style={{ fontSize: 17, lineHeight: 1 }}>{it.icon}</span>
          <span
            style={{
              width: "100%",
              fontSize: 11,
              lineHeight: 1.25,
              textAlign: "center",
              // НЕ nowrap: на узких экранах длинное «Из избранного» должно
              // переноситься на две строки, а не распирать ячейку 1fr до
              // нечитаемого или вылезать за рамку кнопки.
              overflowWrap: "anywhere",
            }}
          >
            {it.label}
          </span>
        </button>
        ),
      )}
    </div>
  );
}

/**
 * Выпадающее меню режимов уведомлений — заменяет двухпозиционный «Mute».
 *
 * Почему меню, а не переключатель: между «всё» и «тихо» есть третий, самый
 * нужный режим — «только упоминания». Кнопка его выразить не могла, и
 * человеку пришлось бы лезть в секцию ниже. Записывается всё в те же
 * серверные push-правила, поэтому выбор действует и на телефоне.
 */
function NotifyMenu({
  roomId,
  mode,
  busy,
  onChoose,
  testId,
}: {
  roomId: string;
  mode: RoomPushMode;
  busy: boolean;
  onChoose: (m: RoomPushMode) => Promise<void>;
  testId: string;
}) {
  const t = useT();
  const tfmt = useTF();
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState<SoundId | "default">(
    () => roomNotificationSetting(roomId)?.sound ?? "default",
  );
  const OPTIONS: { value: RoomPushMode; icon: ReactNode; label: string; hint: string }[] = [
    { value: "all", icon: <IconBell size={18} />, label: t("room.notifyAll"), hint: t("room.notifyAllHint") },
    { value: "mentions", icon: "@", label: t("room.notifyMentions"), hint: t("room.notifyMentionsHint") },
    { value: "muted", icon: <IconBellOff size={18} />, label: t("room.notifyNone"), hint: t("room.notifyNoneHint") },
  ];
  const current = OPTIONS.find((o) => o.value === mode) ?? OPTIONS[0];
  const persistSound = (next: SoundId | "default") => {
    setSound(next);
    saveRoomNotificationSetting(roomId, {
      mode: mode === "muted" ? "mute" : mode,
      ...(next === "default" ? {} : { sound: next }),
    });
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={open}
        title={tfmt("room.notificationsCurrent", { mode: current.label })}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 4,
          padding: "10px 2px",
          width: "100%",
          border: `1px solid ${color.border}`,
          borderRadius: radius.md,
          background: color.bg,
          color: busy ? color.textDim : color.accent,
          cursor: busy ? "default" : "pointer",
          transition: "background 0.15s, border-color 0.15s",
        }}
        onMouseEnter={(e) => {
          if (!busy) {
            e.currentTarget.style.background = color.hover;
            e.currentTarget.style.borderColor = color.accent;
          }
        }}
        onMouseLeave={(e) => {
          if (!busy) {
            e.currentTarget.style.background = color.bg;
            e.currentTarget.style.borderColor = color.border;
          }
        }}
      >
        <span style={{ height: 28, display: "grid", placeItems: "center", fontSize: 22, lineHeight: 1 }}>
          {mode === "muted" ? <IconBellOff size={28} /> : <IconBell size={28} />}
        </span>
        <span style={{ width: "100%", fontSize: 11, textAlign: "center", whiteSpace: "nowrap" }}>{t("room.notificationsMenu")}</span>
      </button>

      {open && (
        <>
          {/* Клик мимо закрывает меню. Прозрачная подложка — самый простой
              способ, не требующий слушателя на document. */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 5 }}
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            data-testid="quick-notify-menu"
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              minWidth: 210,
              zIndex: 6,
              background: color.bg,
              border: `1px solid ${color.border}`,
              borderRadius: radius.md,
              boxShadow: "0 8px 24px rgba(20,24,31,.16)",
              overflow: "hidden",
            }}
          >
            {OPTIONS.map((o) => {
              const on = o.value === mode;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  data-testid={`notify-${o.value}`}
                  onClick={() => {
                    setOpen(false);
                    void onChoose(o.value).then(() => {
                      saveRoomNotificationSetting(roomId, {
                        mode: o.value === "muted" ? "mute" : o.value,
                        ...(sound === "default" ? {} : { sound }),
                      });
                    });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    width: "100%",
                    padding: "8px 10px",
                    border: "none",
                    background: on ? color.accentSoft : "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => {
                    if (!on) e.currentTarget.style.background = color.hover;
                  }}
                  onMouseLeave={(e) => {
                    if (!on) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{ fontSize: 14, width: 16 }}>{o.icon}</span>
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: font.small,
                        fontWeight: on ? 700 : 400,
                        color: color.text,
                      }}
                    >
                      {o.label}
                    </span>
                    <span style={{ display: "block", fontSize: 11, color: color.textDim }}>
                      {o.hint}
                    </span>
                  </span>
                  {on && <span style={{ marginLeft: "auto", color: color.accent }}>✓</span>}
                </button>
              );
            })}
            <div style={{ borderTop: `1px solid ${color.border}`, padding: "9px 10px 10px" }}>
              <label style={{ display: "block", fontSize: 11, color: color.textDim }}>
                {t("notification.roomSoundLabel")}
                <select
                  value={sound}
                  aria-label={t("notification.roomSoundLabel")}
                  onChange={(e) => persistSound(e.target.value as SoundId | "default")}
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: "6px 7px",
                    border: `1px solid ${color.border}`,
                    borderRadius: radius.sm,
                    background: color.bg,
                    color: color.text,
                  }}
                >
                  <option value="default">{t("notification.soundDefault")}</option>
                  {SOUND_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.id === "soft"
                        ? t("notification.soundSoft")
                        : option.id === "double"
                          ? t("notification.soundDouble")
                          : t("notification.soundChime")}
                    </option>
                  ))}
                </select>
              </label>
              {sound !== "default" && (
                <button
                  type="button"
                  onClick={() => previewSound(sound)}
                  style={{
                    marginTop: 7,
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${color.border}`,
                    borderRadius: radius.sm,
                    background: color.bg,
                    color: color.text,
                    cursor: "pointer",
                  }}
                >
                  {t("notification.previewSound")}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * ШАПКА СТРАНИЦЫ КОМНАТЫ.
 *
 * АВАТАР ПРЯМО ОТСЮДА (Антон 2026-09-12 12:49: «в комнате надо тоже
 * иметь возможность загрузить аватар и отредактировать... поставь фотоаппаратик
 * рядом с именем комнаты»). Раньше загрузка жила только внутри модалки
 * RoomSettings — то есть через два клика и без визуальной подсказки, что
 * аватар вообще можно менять.
 *
 * Кнопка показывается ТОЛЬКО при canChangeAvatar: без прав сервер
 * ответит 403, а кликабельный фотоаппарат, который всегда даёт ошибку, хуже
 * отсутствующей кнопки.
 */
function PageHead({
  client,
  room,
  encrypted,
  canEditAvatar,
}: {
  client: MatrixClient;
  room: Room;
  encrypted: boolean;
  canEditAvatar: boolean;
}) {
  const t = useT();
  const avatarEvent = room.currentState.getStateEvents(EventType.RoomAvatar, "");
  const avatarMxc = (avatarEvent?.getContent()?.url as string) ?? null;
  const avatar = useAuthedMedia(client, avatarMxc);
  const topicEvent = room.currentState.getStateEvents(EventType.RoomTopic, "");
  const topic = (topicEvent?.getContent()?.topic as string) ?? "";
  const name = room.name || room.roomId;

  // Состояние загрузки держим здесь, а не в родителе: шапка — единственное
  // место, где этот процесс виден.
  const [avatarBusy, setAvatarBusy] = useState(false);
  // Состояние в замыкании устаревает, ref — нет (см. ProfileView).
  const avatarBusyRef = useRef(false);
  const pickAvatar = async (file: File) => {
    // Раньше здесь был МОЛЧАЛИВЫЙ `if (avatarBusy) return;` — выбранный
    // файл исчезал в никуда без единого слова человеку. Теперь это ошибка,
    // которую видно строкой под кнопкой.
    if (avatarBusyRef.current) throw new Error(t("picker.busy"));
    avatarBusyRef.current = true;
    setAvatarBusy(true);
    try {
      await setRoomAvatar(client, room, file);
    } catch (e) {
      // Ошибку говорим текстом под аватаром, а не молча: типовой случай —
      // слишком большой файл или отказ сервера по правам.
      throw new Error(humanError(e));
    } finally {
      // Сброс ВСЕГДА, включая ветки ошибок и ранние исключения.
      avatarBusyRef.current = false;
      setAvatarBusy(false);
    }
  };

  // Единый механизм выбора файла (см. useImagePicker). id больше не клеится из
  // roomId: та же комната, показанная дважды (шапка + тред/панель), давала дубль id.
  const picker = useImagePicker({ onFile: pickAvatar });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: space.md,
        padding: `0 ${space.xl}px ${space.xl}px`,
        textAlign: "center",
      }}
    >
      {/* АВАТАР + ФОТОАППАРАТ. position: relative нужен для значка,
          который сидит в правом нижнем углу кружка — как в Telegram/WhatsApp. */}
      <div style={{ position: "relative", flex: "0 0 auto" }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: radius.circle,
            overflow: "hidden",
            background: colorOf(room.roomId),
            display: "grid",
            placeItems: "center",
            color: color.textOnAccent,
            fontSize: 32,
            fontWeight: font.weightBold,
            userSelect: "none",
            opacity: avatarBusy ? 0.5 : 1,
          }}
        >
          {avatar.url ? (
            <img
              src={avatar.url}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            name.replace(/^[#!@]/, "").slice(0, 2).toUpperCase()
          )}
        </div>

        {canEditAvatar && (
          <>
            <input
              ref={picker.ref}
              type="file"
              accept={AVATAR_ACCEPT}
              style={visuallyHiddenInput}
              id={picker.id}
              tabIndex={-1}
              aria-hidden="true"
              data-testid="room-head-avatar-input"
              onChange={picker.onChange}
            />
            {/* Была <label htmlFor>: при дубле id в DOM она цеплялась к чужому
                input. Теперь кнопка + ref.click(), как в остальных двух местах. */}
            {/* disabled УБРАН (Антон 24.09, второй заход): заблокированный
                фотоаппарат не даёт ни клика, ни диалога, молча. Выбрать файл
                можно всегда; повторную отправку сдерживает avatarBusyRef. */}
            <button
              type="button"
              onClick={picker.open}
              title={avatarBusy ? t("ui.zagruzka") : t("ui.zagruzit_avatar_komnaty")}
              data-testid="room-head-avatar-button"
              style={{
                position: "absolute",
                right: -2,
                bottom: -2,
                width: 32,
                height: 32,
                borderRadius: radius.circle,
                background: color.accentSoft,
                color: color.accent,
                border: `1px solid ${color.accent}`,
                display: "grid",
                placeItems: "center",
                fontSize: 15,
                lineHeight: 1,
                cursor: "pointer",
                opacity: avatarBusy ? 0.6 : 1,
                // Обводка цветом фона — значок не сливается с тёмным фото.
                boxShadow: `0 0 0 2px ${color.bg}`,
                userSelect: "none",
              }}
            >
              {avatarBusy ? "⋯" : <IconCamera size={17} />}
            </button>
          </>
        )}
      </div>

      {/* Видимая диагностика вместо отдельной строки ошибки: теперь сюда
          пишутся и ошибки, и ход дела (нажатие, выбор файла, отправка). */}
      {canEditAvatar && (
        <div style={{ textAlign: "center" }}>
          <PickerNoteLine note={picker.note} testId="room-head-avatar-note" />
        </div>
      )}
      <div style={{ minWidth: 0, maxWidth: "100%" }}>
        <div
          style={{ fontSize: 16, fontWeight: font.weightBold, color: color.text, wordBreak: "break-word" }}
          data-testid="room-page-title"
        >
          {encrypted && "🔒 "}
          {name}
        </div>
        <div style={{ fontSize: font.small, color: color.textDim, marginTop: 2 }}>
          {room.getJoinedMemberCount()} {membersWord(room.getJoinedMemberCount(), t)}
        </div>
        {topic && (
          <div
            style={{
              fontSize: font.small,
              color: color.textDim,
              marginTop: space.md,
              lineHeight: 1.4,
              wordBreak: "break-word",
            }}
            data-testid="room-page-topic"
          >
            {topic}
          </div>
        )}
      </div>
    </div>
  );
}

/** Секция «Настройки комнаты»: вход в редактор полей + честные недоступные. */
function SettingsSection({
  client,
  room,
  canEditSomething,
  onOpenSettings,
}: {
  client: MatrixClient;
  room: Room;
  canEditSomething: boolean;
  onOpenSettings: () => void;
}) {
  const t = useT();
  const perms = useRoomPermissions(client, room);
  const nameEvent = room.currentState.getStateEvents(EventType.RoomName, "");
  const topicEvent = room.currentState.getStateEvents(EventType.RoomTopic, "");
  const joinRulesEvent = room.currentState.getStateEvents("m.room.join_rules", "");
  const historyEvent = room.currentState.getStateEvents("m.room.history_visibility", "");

  const currentName = (nameEvent?.getContent()?.name as string) ?? "";
  const currentTopic = (topicEvent?.getContent()?.topic as string) ?? "";
  const currentJoinRule = (joinRulesEvent?.getContent()?.join_rule as string) ?? "invite";
  const currentHistory = (historyEvent?.getContent()?.history_visibility as string) ?? "shared";

  // Подписи — по значениям спеки m.room.history_visibility, все четыре:
  // показываем факт, а не только два самых частых.
  const historyLabel =
    currentHistory === "world_readable"
      ? t("ui.vidna_vsem_dazhe_bez_vhoda")
      : currentHistory === "shared"
        ? t("ui.s_momenta_vhoda_uchastnika")
        : currentHistory === "invited"
          ? t("ui.s_momenta_priglasheniya")
          : t("ui.tolko_s_momenta_vhoda");

  return (
    <>
      <div style={s.sectionLabel}>{t("room.roomSettings")}</div>
      <div style={{ padding: `0 ${space.xl}px`, fontSize: font.small }}>
        <SettingLine label={t("dialog.newRoomNameLabel")} value={currentName || t("dialog.untitled")} allowed={perms.canChangeName} />
        <SettingLine label={t("ui.tema")} value={currentTopic || t("dialog.topicUnset")} allowed={perms.canChangeTopic} />
        <SettingLine label={t("ui.avatar")} value="" allowed={perms.canChangeAvatar} />
        <SettingLine
          label={t("dialog.joinRoom")}
          value={currentJoinRule === "public" ? t("ui.publichnaya") : t("ui.po_priglasheniyu")}
          allowed={perms.canChangeJoinRules}
        />
        <SettingLine
          label={t("dialog.historyVisibility")}
          value={historyLabel}
          allowed={perms.canChangeHistoryVisibility}
        />
        {canEditSomething ? (
          <button
            type="button"
            style={{ ...s.actionButton, marginTop: space.md, transition: "background 0.12s" }}
            onClick={onOpenSettings}
            data-testid="page-open-settings"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = color.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = color.bg;
            }}
          >{t("room.changeSettings")}</button>
        ) : (
          <div style={{ fontSize: font.micro, color: color.textDim, marginTop: space.md, lineHeight: 1.4 }}>{t("room.noRightsSettings")}</div>
        )}
      </div>
    </>
  );
}

/** Одна строка настройки: лейбл, текущее значение, пометка о недоступности. */
function SettingLine({ label, value, allowed }: { label: string; value: string; allowed: boolean }) {
  const t = useT();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: space.md,
        padding: "3px 0",
        opacity: allowed ? 1 : 0.65,
      }}
      data-testid={`setting-${label}`}
      data-allowed={allowed ? "1" : "0"}
    >
      <span style={{ flex: "0 0 120px", color: color.textDim }}>{label}</span>
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          color: color.text,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </span>
      {!allowed && (
        <span style={{ flex: "0 0 auto", fontSize: font.micro, color: color.textDim }} title={t("room.insufficientPermissions")}>{t("room.noRights")}</span>
      )}
    </div>
  );
}

/** Секция «Права ролей»: четыре порога из m.room.power_levels. */
function PowerLevelsSection({
  client,
  room,
  canChange,
  myPowerLevel,
}: {
  client: MatrixClient;
  room: Room;
  canChange: boolean;
  myPowerLevel: number;
}) {
  const t = useT();
  const tfmt = useTF();
  // Реактивность обеспечивает РОДИТЕЛЬ: RoomPage вызывает useRoomPermissions,
  // который подписан на RoomStateEvent.Events, и на изменение power_levels
  // перерисует всю панель вместе с этой секцией. Своей подписки здесь нет
  // сознательно — не плодим вторую там, где одна уже гарантирована.
  const pl = powerLevelsOf(room);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows: {
    key: "m.room.message" | "invite" | "redact" | "m.room.power_levels";
    label: string;
  }[] = [
    { key: "m.room.message", label: t("ui.pisat_soobscheniya") },
    { key: "invite", label: t("ui.priglashat_uchastnikov") },
    { key: "redact", label: t("ui.udalyat_chuzhie_soobscheniya") },
    { key: "m.room.power_levels", label: t("ui.menyat_nastroyki_i_roli") },
  ];

  const choices = [
    { level: 0, label: t("ui.uchastniki") },
    { level: 50, label: t("ui.moderatory") },
    { level: 100, label: t("ui.adminy") },
  ];

  const change = async (key: (typeof rows)[number]["key"], level: number) => {
    if (busyKey) return;
    setBusyKey(key);
    setError(null);
    try {
      await setPowerLevelThreshold(client, room, key, level);
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <>
      <div style={s.sectionLabel}>{t("room.rolePermissions")}</div>
      <div style={{ padding: `0 ${space.xl}px`, fontSize: font.small }} data-testid="page-power-levels">
        {rows.map((r) => {
          const current = thresholdOf(pl, r.key);
          return (
            <label
              key={r.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: space.md,
                padding: "4px 0",
                opacity: canChange ? 1 : 0.65,
              }}
            >
              <span style={{ flex: "1 1 auto", minWidth: 0, color: color.text }}>{r.label}</span>
              <select
                style={{ ...s.dialogInput, width: "auto", marginTop: 0, padding: "3px 6px", fontSize: font.micro }}
                value={current}
                disabled={!canChange || busyKey !== null}
                onChange={(e) => void change(r.key, Number(e.target.value))}
                data-testid={`pl-${r.key}`}
                title={canChange ? undefined : t("ui.nedostatochno_prav_dlya_izmeneniya")}
              >
                {choices.map((c) => (
                  <option key={c.level} value={c.level}>
                    {c.label} ({c.level})
                  </option>
                ))}
                {/* Текущее значение может не совпадать с тремя пресетами
                    (например 25, поставленное другим клиентом) — показываем
                    его честно, а не подменяем ближайшим пресетом. */}
                {!choices.some((c) => c.level === current) && (
                  <option value={current}>{tfmt("room.levelValue", { level: current })}</option>
                )}
              </select>
            </label>
          );
        })}
        {error && (
          <div style={{ ...s.dialogError, marginTop: space.sm }} role="alert" data-testid="pl-error">
            {error}
          </div>
        )}
        <div style={{ fontSize: font.micro, color: color.textDim, marginTop: space.sm, lineHeight: 1.4 }}>
          {canChange
            ? tfmt("room.yourPowerLevel", { level: myPowerLevel })
            : t("ui.menyat_prava_roley_mozhet_tolko_tot_u_ko")}
        </div>
      </div>
    </>
  );
}

/**
 * Сворачиваемый админский блок внизу страницы комнаты.
 *
 * Антон 10.09 17:26: «отсюда и ниже надо скрыть» — про настройки комнаты,
 * пороги прав и статус шифрования. Это админские вещи: нужны редко,
 * а места занимали больше, чем всё остальное вместе взятое.
 *
 * Скрыты, а не удалены: второго входа к ним в клиенте не существует,
 * и человек с правами должен иметь способ туда попасть.
 */
function AdvancedDisclosure({ children }: { children: ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <SectionDivider />
      <button
        type="button"
        style={{ ...s.roomPageRow, transition: "background 0.12s" }}
        onClick={() => setOpen((v) => !v)}
        data-testid="page-advanced-toggle"
        aria-expanded={open}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = color.hover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <span style={{ width: 24, textAlign: "center", flex: "0 0 24px" }} aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        <span style={{ flex: "1 1 auto", minWidth: 0, textAlign: "left" }}>{t("room.advancedSettings")}<span style={{ display: "block", fontSize: font.micro, color: color.textDim }}>{t("room.settingsSubtitle")}</span>
        </span>
      </button>
      {open && children}
    </>
  );
}

function SectionDivider() {
  return <div style={s.sectionDivider} />;
}

/**
 * Скелетоны вместо текста «Смотрю историю…» (образец Антона 10.09).
 *
 * Почему не строка текста: пока идёт первый проход по таймлайну (сотни
 * сообщений), сухая подпись выглядела как зависшая панель — человек не
 * понимал, идёт ли работа. Пульсирующие прямоугольники читаются как
 * «загружается» без слов. Подпись оставлена ПОД ними, потому что одно
 * мерцание без текста не объясняет, ЧТО именно грузится.
 * Пульсация 1.2s ease-in-out infinite — мягкая, не стробоскоп.
 */
function ScanSkeleton() {
  const t = useT();
  return (
    <div style={{ padding: space.md }} data-testid="scan-skeleton">
      <style>{`@keyframes algSkeletonPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: 44,
            marginBottom: space.md,
            borderRadius: radius.md,
            background: color.hover,
            // Разная ширина строк — читается как контент, а не как решётка.
            width: ["92%", "100%", "78%", "85%"][i],
            animation: "algSkeletonPulse 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
      <div style={{ fontSize: font.small, color: color.textDim }}>{t("room.loadingHistory")}</div>
    </div>
  );
}
